import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { addMonths, addYears, format, isValid, parse, setYear } from 'date-fns';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { DayPicker, useDayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';

import { Input } from './input';

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  id?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  /** Forwarded to the inner `<Input className>` (the button trigger's `className` above is unaffected). */
  inputClassName?: string;
  /**
   * `md` (default) — tall py-5 field matching auth/marketing forms.
   * `sm` — dense py-2.5 field (~40px) that aligns with Select/Input sm in admin forms.
   */
  size?: 'md' | 'sm';
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

function toDate(v?: string): Date | undefined {
  if (!v) return undefined;
  const d = parse(v, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : undefined;
}

const NAV_BTN =
  'size-8 rounded-[8px] border border-neutral-200 bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.05)] inline-grid place-content-center text-accent-ink hover:bg-primary-50 disabled:opacity-40 disabled:cursor-not-allowed';

const YEARS_PER_PAGE = 12;

/** Anchor 12-year pages to multiples of 12 so navigation is deterministic. */
function pageStartFor(year: number): number {
  return Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;
}

/**
 * Bridge for the day-view header to open the year picker. Lives in module
 * scope so the MonthCaption wrapper we hand to react-day-picker is also at
 * module scope (defining components inside components trips a lint rule).
 */
const OpenYearViewContext = React.createContext<(() => void) | null>(null);

function MonthCaptionBridge(props: { calendarMonth: { date: Date } }) {
  const openYearView = React.useContext(OpenYearViewContext);
  return (
    <DayMonthCaption
      calendarMonth={props.calendarMonth}
      onOpenYearView={openYearView ?? (() => undefined)}
    />
  );
}

/**
 * Day-view header: matches the Figma calendar:
 *   [<<][<]   Month YYYY ▾   [>][>>]
 * The "Month YYYY" label is a button that drills into the year picker —
 * tapping `<` 36 times to reach a date-of-birth is a UX trap; the year grid
 * makes the jump in two clicks.
 */
function DayMonthCaption({
  calendarMonth,
  onOpenYearView,
}: {
  calendarMonth: { date: Date };
  onOpenYearView: () => void;
}) {
  const ctx = useDayPicker();
  const month = calendarMonth.date;

  const goTo = (next: Date) => {
    ctx.goToMonth(next);
  };

  return (
    <div className="relative flex items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous year"
          className={NAV_BTN}
          onClick={() => {
            goTo(addYears(month, -1));
          }}
        >
          <ChevronsLeft className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Previous month"
          className={NAV_BTN}
          onClick={() => {
            goTo(addMonths(month, -1));
          }}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>
      </div>

      <button
        type="button"
        onClick={onOpenYearView}
        aria-label={`Select year — showing ${format(month, 'MMMM yyyy')}`}
        className="text-accent-ink hover:bg-primary-50 focus-visible:ring-primary-200 inline-flex items-center gap-1 rounded-[6px] px-2 py-1 font-sans text-sm leading-none font-medium tracking-tight whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none"
      >
        <span>{format(month, 'LLLL')}</span>
        <span className="text-neutral-500 tabular-nums">{format(month, 'yyyy')}</span>
        <ChevronDown className="size-3.5 text-neutral-500" aria-hidden="true" />
      </button>

      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Next month"
          className={NAV_BTN}
          onClick={() => {
            goTo(addMonths(month, 1));
          }}
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Next year"
          className={NAV_BTN}
          onClick={() => {
            goTo(addYears(month, 1));
          }}
        >
          <ChevronsRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Year picker view. Click "Month YYYY" in the day-view header to open this.
 * Click a year → jump to day view in that year (same month).
 * Click the page-range header (e.g. "2016–2027") → return to day view.
 *
 * Keyboard: arrow keys move between cells; Enter/Space selects; Esc returns
 * to day view. Initial focus is the selected (or current) year.
 */
function YearView({
  centerYear,
  selectedYear,
  todayYear,
  minYear,
  maxYear,
  onSelect,
  onBack,
}: {
  centerYear: number;
  selectedYear?: number;
  todayYear: number;
  minYear?: number;
  maxYear?: number;
  onSelect: (year: number) => void;
  onBack: () => void;
}) {
  const [pageStart, setPageStart] = React.useState(() => pageStartFor(centerYear));
  const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i);

  const gridRef = React.useRef<HTMLTableElement | null>(null);

  // Arrow-key navigation within the visible 12-year page. We deliberately
  // don't flip pages on edge-presses — that would yank focus out of the
  // re-rendered grid, which dismisses Radix's popover focus scope. Users
  // cross page boundaries via the prev/next page buttons (which keep their
  // own focus when clicked).
  const handleKey = (e: React.KeyboardEvent<HTMLTableElement>) => {
    const grid = gridRef.current;
    if (!grid) return;
    const active = document.activeElement as HTMLButtonElement | null;
    if (!active?.dataset.year) return;
    const currentIdx = years.indexOf(Number(active.dataset.year));
    if (currentIdx < 0) return;

    const moveTo = (idx: number) => {
      const next = grid.querySelector<HTMLButtonElement>(
        `button[data-cell-idx="${String(idx)}"]:not(:disabled)`,
      );
      next?.focus();
    };

    switch (e.key) {
      case 'ArrowLeft':
        if (currentIdx > 0) {
          e.preventDefault();
          moveTo(currentIdx - 1);
        }
        break;
      case 'ArrowRight':
        if (currentIdx < YEARS_PER_PAGE - 1) {
          e.preventDefault();
          moveTo(currentIdx + 1);
        }
        break;
      case 'ArrowUp':
        if (currentIdx >= 4) {
          e.preventDefault();
          moveTo(currentIdx - 4);
        }
        break;
      case 'ArrowDown':
        if (currentIdx < YEARS_PER_PAGE - 4) {
          e.preventDefault();
          moveTo(currentIdx + 4);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onBack();
        break;
    }
  };

  const pageStartDisabled = minYear !== undefined && pageStart - 1 < minYear;
  const pageEndDisabled = maxYear !== undefined && pageStart + YEARS_PER_PAGE > maxYear;

  return (
    <div className="p-2">
      <div className="relative flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous years"
          className={NAV_BTN}
          disabled={pageStartDisabled}
          onClick={() => {
            setPageStart((p) => p - YEARS_PER_PAGE);
          }}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={onBack}
          aria-label="Back to month view"
          className="text-accent-ink hover:bg-primary-50 focus-visible:ring-primary-200 inline-flex items-center gap-1 rounded-[6px] px-2 py-1 font-sans text-sm leading-none font-medium tracking-tight whitespace-nowrap tabular-nums focus-visible:ring-2 focus-visible:outline-none"
        >
          {pageStart}
          <span className="text-neutral-400">–</span>
          {pageStart + YEARS_PER_PAGE - 1}
        </button>

        <button
          type="button"
          aria-label="Next years"
          className={NAV_BTN}
          disabled={pageEndDisabled}
          onClick={() => {
            setPageStart((p) => p + YEARS_PER_PAGE);
          }}
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>

      <table
        ref={gridRef}
        aria-label="Year picker"
        onKeyDown={handleKey}
        className="mt-5 w-full border-collapse"
      >
        <tbody>
          {[0, 1, 2].map((rowIdx) => (
            <tr
              key={rowIdx}
              className="flex w-full justify-between gap-2 [&:not(:first-child)]:mt-2"
            >
              {years.slice(rowIdx * 4, rowIdx * 4 + 4).map((year, colIdx) => {
                const idx = rowIdx * 4 + colIdx;
                const disabled =
                  (minYear !== undefined && year < minYear) ||
                  (maxYear !== undefined && year > maxYear);
                const isSelected = year === selectedYear;
                const isToday = year === todayYear;
                return (
                  <td key={year} className="flex-1">
                    <button
                      type="button"
                      data-year={year}
                      data-cell-idx={idx}
                      aria-pressed={isSelected}
                      aria-current={isToday ? 'date' : undefined}
                      disabled={disabled}
                      onClick={() => {
                        onSelect(year);
                      }}
                      className={cn(
                        'h-10 w-full rounded-[8px] font-sans text-base leading-[22px] font-normal tabular-nums transition-colors',
                        'hover:bg-primary-50 focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:outline-none',
                        isSelected
                          ? 'bg-primary-300 text-primary-50 hover:bg-primary-300'
                          : 'text-primary-700',
                        isToday &&
                          !isSelected &&
                          'ring-primary-200 font-semibold ring-1 ring-inset',
                        disabled &&
                          'cursor-not-allowed text-neutral-400 opacity-50 hover:bg-transparent',
                      )}
                    >
                      {year}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      onBlur,
      name,
      id,
      placeholder = 'dd/mm/yyyy',
      min,
      max,
      disabled,
      className,
      inputClassName,
      size = 'md',
      ...aria
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const selected = toDate(value);
    const minDate = toDate(min);
    const maxDate = toDate(max ?? new Date().toISOString().slice(0, 10));

    // View state lives outside DayPicker so the year grid can replace the day grid.
    const [view, setView] = React.useState<'day' | 'year'>('day');
    const [focusedMonth, setFocusedMonth] = React.useState<Date>(() => selected ?? new Date());

    // Reset view + sync focused month each time the popover opens, so a
    // previously-opened year view doesn't linger on reopen. Depend on the
    // raw string `value`/`min`/`max` instead of the parsed Date objects —
    // toDate() returns a fresh Date each render, which would loop the
    // effect.
    //
    // No value yet — open onto a sensible "current" month. Default to today,
    // unless today is outside [min, max] in which case clamp to the nearer
    // bound so the user doesn't land on a fully-disabled grid. The earlier
    // "always focus max" rule was correct for DOB pickers (max = 18y ago, so
    // today falls past max) but wrong for forward-looking pickers like the
    // assessments bulk-assign due date (max = 2099-12-31, min = today) — the
    // picker opened at December 2099 instead of the current month.
    React.useEffect(() => {
      if (!open) return;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset-on-open must also re-sync when value/min/max change while the popover is open; an onOpenChange handler cannot observe prop changes
      setView('day');
      const parsed = toDate(value);
      if (parsed) {
        setFocusedMonth(parsed);
        return;
      }
      const today = new Date();
      const minDate = toDate(min);
      const maxDate = toDate(max);
      if (maxDate && today > maxDate) {
        setFocusedMonth(maxDate);
      } else if (minDate && today < minDate) {
        setFocusedMonth(minDate);
      } else {
        setFocusedMonth(today);
      }
    }, [open, value, min, max]);

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            onClick={() => {
              if (!disabled) setOpen((v) => !v);
            }}
            disabled={disabled}
            className={cn('w-full text-left', className)}
          >
            <Input
              ref={ref}
              type="text"
              readOnly
              name={name}
              id={id}
              size={size}
              className={inputClassName}
              placeholder={placeholder}
              value={selected ? format(selected, 'dd/MM/yyyy') : ''}
              onBlur={onBlur}
              disabled={disabled}
              endIcon={<Calendar className="size-3.5" aria-hidden="true" />}
              {...aria}
            />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            className="border-primary-100 bg-surface z-50 w-80 rounded-lg border p-3 shadow-[0_2px_4px_-2px_rgb(15_23_42/0.06),0_18px_40px_-16px_rgb(2_87_67/0.28)]"
          >
            {view === 'day' ? (
              <OpenYearViewContext.Provider
                value={() => {
                  setView('year');
                }}
              >
                <DayPicker
                  mode="single"
                  month={focusedMonth}
                  onMonthChange={setFocusedMonth}
                  selected={selected}
                  onSelect={(d) => {
                    if (d) {
                      onChange?.(format(d, 'yyyy-MM-dd'));
                      setOpen(false);
                    }
                  }}
                  disabled={[
                    ...(minDate ? [{ before: minDate }] : []),
                    ...(maxDate ? [{ after: maxDate }] : []),
                  ]}
                  showOutsideDays
                  weekStartsOn={1}
                  classNames={{
                    root: 'p-2',
                    nav: 'hidden',
                    months: 'flex flex-col gap-5',
                    month: 'flex w-full flex-col gap-5',
                    month_grid: 'w-full border-collapse',
                    weekdays: 'flex w-full justify-between',
                    weekday:
                      'flex-1 font-sans text-base font-normal text-neutral-400 leading-[22px] text-center',
                    week: 'flex w-full justify-between mt-2',
                    day: 'flex-1 flex items-center justify-center h-8',
                    day_button:
                      'size-8 rounded-[8px] font-sans text-base font-normal text-primary-700 leading-[22px] hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200',
                    selected:
                      '[&_button]:bg-primary-300 [&_button]:text-primary-50 [&_button]:hover:bg-primary-300',
                    today: '[&_button]:font-semibold',
                    outside: '[&_button]:text-neutral-400',
                    disabled: '[&_button]:text-neutral-400 [&_button]:opacity-50',
                  }}
                  components={{ MonthCaption: MonthCaptionBridge }}
                />
              </OpenYearViewContext.Provider>
            ) : (
              <YearView
                centerYear={focusedMonth.getFullYear()}
                selectedYear={selected?.getFullYear()}
                todayYear={new Date().getFullYear()}
                minYear={minDate?.getFullYear()}
                maxYear={maxDate?.getFullYear()}
                onSelect={(year) => {
                  setFocusedMonth((m) => setYear(m, year));
                  setView('day');
                }}
                onBack={() => {
                  setView('day');
                }}
              />
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  },
);
DatePicker.displayName = 'DatePicker';
